import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  WebSocketComponent,
  setSelectedDealer,
  setSelectedLocation,
  useAllSelectedLocations,
  useIsAllLocationsSelected,
  usePermanentRealtimeDealers,
  useSelectedDealer,
  useSelectedLocation
} from "components";
import { LOCATION_DROPDOWN_STORAGE_KEYS } from "components/LocationDropdown/storageKeys";
import { useWebSocketStatus } from "components/WebSocket/hooks/useWebSocketStatus";
import { useAppVersionCheck, usePermanentRealTimeUser } from "hooks";
import { ROLES } from "models";
import { useRealTimeAppointmentsQuery } from "modules/Appointments/hooks";
import { usePermanentSession } from "modules/Login/hooks";
import { LOGIN_STORAGE_KEYS } from "modules/Login/storageKeys";
import { useIsUserDPOAndMustSign } from "modules/PrivacyAndSecurityDashboard/hooks";
import { loginZohoDeskASAP } from "plugins/ZohoDeskASAP";
import { PATHS } from "router/paths";
import { getPreference, setPreference } from "utils/common";
import { clearDashboardFiltersValues } from "utils/storageUtil";

import type { Dealer, DealerLocation, UserID } from "models";

const DEALER_LOCATION_PRIVATE_NOTIFIER_KEY = "93b6a03c2b9213534cbc68a453590b86c52a289871020d09f4a144fc64cd8b31";

export const usePrivateRouteSetup = () => {
  const history = useHistory();

  const [isReady, setIsReady] = useState(false);
  useRealTimeAppointmentsQuery();
  useAppVersionCheck();

  const { dealers, isFetched: isDealerFetched } = usePermanentRealtimeDealers();
  const { user, isFetched: isUserFetched } = usePermanentRealTimeUser();
  const isUserDPOAndMustSign = useIsUserDPOAndMustSign(user);
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const { isConnected } = useWebSocketStatus();
  const { isAllLocationsSelected } = useIsAllLocationsSelected();
  const allSelectedLocations = useAllSelectedLocations(user);
  const { session } = usePermanentSession();

  const locationPreference = getPreference<DealerLocation | null>(LOCATION_DROPDOWN_STORAGE_KEYS.LocationSelectedPreference, null);
  const dealerPreference = getPreference<Dealer | null>(LOCATION_DROPDOWN_STORAGE_KEYS.DealerSelectedPreference, null);

  useEffect(() => {
    if (!session?.zoho_unameit_asap_jwt || !isDealerFetched || !isUserFetched) return;
    if (user?.role_id === ROLES.CounterTablet) {
      setIsReady(true);
      return;
    }
    if (!user || !dealers.length) return;

    const selectedDealer = dealers.find(d => d.id === dealerPreference?.id) ?? dealers.find(d => d.id === user.dealer_id);
    if (!selectedDealer) return;

    const selectedLocation =
      selectedDealer.locations?.find(dl => dl.id === locationPreference?.id) ?? selectedDealer.locations?.find(dl => dl.id === user.dealer_location_id);
    if (!selectedLocation) return;

    setSelectedDealer(selectedDealer);
    setSelectedLocation(selectedLocation);

    const storedUserId = getPreference<UserID | null>(LOGIN_STORAGE_KEYS.UserIdPreference, null);
    if (storedUserId && storedUserId !== user.id) clearDashboardFiltersValues();

    setPreference(LOGIN_STORAGE_KEYS.UserIdPreference, user.id);
    WebSocketComponent.connect(user.id);
    loginZohoDeskASAP(session.zoho_unameit_asap_jwt);
    setIsReady(true);
  }, [session?.zoho_unameit_asap_jwt, isDealerFetched, isUserFetched]);

  useEffect(() => {
    if (!selectedLocation || !isConnected) return;

    if (isAllLocationsSelected) {
      allSelectedLocations.forEach(location => {
        WebSocketComponent.subscribeToLocationQueue(location.notifier_key); // TODO: this is not unsubscribed from
      });
    } else {
      WebSocketComponent.subscribeToLocationQueue(selectedLocation.notifier_key);
    }

    WebSocketComponent.subscribeToLocationQueue(DEALER_LOCATION_PRIVATE_NOTIFIER_KEY);

    return () => {
      WebSocketComponent.unsubscribeFromLocationQueue(selectedLocation.notifier_key);
      WebSocketComponent.unsubscribeFromLocationQueue(DEALER_LOCATION_PRIVATE_NOTIFIER_KEY);
    };
  }, [selectedLocation, isConnected, isAllLocationsSelected]);

  useEffect(() => {
    const dealerNotifierKey = selectedDealer?.notifier_key;
    if (!dealerNotifierKey || !isConnected) return;

    WebSocketComponent.subscribeToDealerQueue(dealerNotifierKey);

    return () => WebSocketComponent.unsubscribeFromDealerQueue(dealerNotifierKey);
  }, [selectedDealer?.notifier_key, isConnected]);

  useEffect(() => {
    if (isUserDPOAndMustSign) return history.push(PATHS.DPO_CONTRACT);
  }, [isUserDPOAndMustSign]);

  return { isReady, selectedLocation };
};
